// apps/web/src/components/TemplateBuilder.js
import React, { useState, useEffect } from 'react';
import { useToast } from './Toast';
import WYSIWYGTemplateBuilder from './WYSIWYGTemplateBuilder';

export default function TemplateBuilder({ editingTemplate, onSave, onCancel }) {
  const toast = useToast();

  const [formData, setFormData] = useState({
    id: '',
    title: '',
    category: 'general',
    audience: '',
    summary: '',
    tags: [],
    pricing_tier: 'free',
    price_gbp: '0.00',
    html: '',
    fields: null,
    is_published: false,
    featured: false,
    variables: [],
    tasks: [],
  });

  const [tagInput, setTagInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // Load editing template if provided
  useEffect(() => {
    if (editingTemplate) {
      // Auto-detect if template was created with WYSIWYG builder
      let fields = editingTemplate.fields || null;
      if (fields?.interactive && fields.variables && fields.tasks && !fields.visual) {
        // This template has the structure of a WYSIWYG template but is missing the visual flag
        // Add it so it loads in visual mode
        fields = { ...fields, visual: true };
      }

      setFormData({
        id: editingTemplate.id || '',
        title: editingTemplate.title || '',
        category: editingTemplate.category || 'general',
        audience: editingTemplate.audience || '',
        summary: editingTemplate.summary || '',
        tags: editingTemplate.tags || [],
        pricing_tier: editingTemplate.pricing_tier || 'free',
        price_gbp: editingTemplate.price_gbp ? parseFloat(editingTemplate.price_gbp).toFixed(2) : '0.00',
        html: editingTemplate.html || '',
        fields: fields,
        is_published: editingTemplate.is_published !== false,
        featured: editingTemplate.featured === true,
      });
    }
  }, [editingTemplate]);

  function handleChange(field, value) {
    setFormData(prev => ({ ...prev, [field]: value }));
  }

  function handleAddTag() {
    const tag = tagInput.trim();
    if (!tag) return;
    if (formData.tags.includes(tag)) {
      toast.error('Tag already exists');
      return;
    }
    setFormData(prev => ({
      ...prev,
      tags: [...prev.tags, tag],
    }));
    setTagInput('');
  }

  function handleRemoveTag(tagToRemove) {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tagToRemove),
    }));
  }

  async function handleSave() {
    // Validation
    if (!formData.title.trim()) {
      toast.error('Template title is required');
      return;
    }

    if (!formData.summary.trim()) {
      toast.error('Template summary is required');
      return;
    }

    if (formData.pricing_tier === 'premium') {
      const price = parseFloat(formData.price_gbp);
      if (isNaN(price) || price <= 0) {
        toast.error('Premium templates must have a price greater than £0.00');
        return;
      }
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('accessToken');
      const isEditing = !!formData.id;

      const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:3001/api/v1';
      const endpoint = isEditing
        ? `${apiBase}/admin/templates/${formData.id}`
        : `${apiBase}/admin/templates`;

      const method = isEditing ? 'PUT' : 'POST';

      const response = await fetch(endpoint, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          title: formData.title.trim(),
          category: formData.category,
          audience: formData.audience.trim() || null,
          summary: formData.summary.trim(),
          tags: formData.tags,
          pricing_tier: formData.pricing_tier,
          price_gbp: formData.pricing_tier === 'premium' ? parseFloat(formData.price_gbp) : 0.00,
          html: formData.html.trim() || null,
          fields: formData.fields,
          is_published: formData.is_published,
          featured: formData.featured,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to save template');
      }

      const result = await response.json();
      toast.success(isEditing ? 'Template updated successfully' : 'Template created successfully');

      if (onSave) {
        onSave(result.template);
      }

      // Reset form if creating new template
      if (!isEditing) {
        setFormData({
          id: '',
          title: '',
          category: 'general',
          audience: '',
          summary: '',
          tags: [],
          pricing_tier: 'free',
          price_gbp: '0.00',
          html: '',
          fields: null,
          is_published: false,
          featured: false,
        });
      }
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error(error.message || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="TemplateBuilder">
      <div className="TemplateBuilderHeader">
        <h2 className="TemplateBuilderTitle">
          {formData.id ? 'Edit Template' : 'Create New Template'}
        </h2>
        <div className="TemplateBuilderActions">
          <button
            className="TemplateBuilderBtn TemplateBuilderBtnSecondary"
            onClick={() => setShowPreview(!showPreview)}
          >
            {showPreview ? '📝 Edit' : '👁️ Preview'}
          </button>
          {onCancel && (
            <button
              className="TemplateBuilderBtn TemplateBuilderBtnSecondary"
              onClick={onCancel}
            >
              Cancel
            </button>
          )}
          <button
            className="TemplateBuilderBtn TemplateBuilderBtnPrimary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : formData.id ? 'Update Template' : 'Create Template'}
          </button>
        </div>
      </div>

      {showPreview ? (
        <div className="TemplateBuilderPreview">
          <div className="TemplateBuilderPreviewHeader">
            <h3>Preview</h3>
          </div>
          <div className="TemplateBuilderPreviewContent">
            <div className="TemplateBuilderPreviewMeta">
              <h2>{formData.title || 'Untitled Template'}</h2>
              {formData.category && (
                <span className="TemplateBuilderPreviewBadge">
                  {formData.category}
                </span>
              )}
              {formData.pricing_tier === 'premium' && (
                <span className="TemplateBuilderPreviewPrice">
                  £{parseFloat(formData.price_gbp).toFixed(2)}
                </span>
              )}
            </div>
            <p className="TemplateBuilderPreviewSummary">
              {formData.summary || 'No summary provided'}
            </p>
            {formData.tags.length > 0 && (
              <div className="TemplateBuilderPreviewTags">
                {formData.tags.map((tag, idx) => (
                  <span key={idx} className="TemplateBuilderPreviewTag">
                    {tag}
                  </span>
                ))}
              </div>
            )}
            {formData.html && (
              <div className="TemplateBuilderPreviewHTML">
                <h4>Template Content:</h4>
                <div dangerouslySetInnerHTML={{ __html: formData.html }} />
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="TemplateBuilderForm">
          {/* Basic Information Section */}
          <section className="TemplateBuilderSection">
            <h3 className="TemplateBuilderSectionTitle">Basic Information</h3>

            <div className="TemplateBuilderField">
              <label className="TemplateBuilderLabel">
                Template Title *
              </label>
              <input
                type="text"
                className="TemplateBuilderInput"
                placeholder="e.g., Project Planning Template"
                value={formData.title}
                onChange={(e) => handleChange('title', e.target.value)}
              />
            </div>

            <div className="TemplateBuilderFieldGroup">
              <div className="TemplateBuilderField">
                <label className="TemplateBuilderLabel">Category *</label>
                <select
                  className="TemplateBuilderSelect"
                  value={formData.category}
                  onChange={(e) => handleChange('category', e.target.value)}
                >
                  <option value="general">General</option>
                  <option value="lecturer_student">Lecturer/Student</option>
                  <option value="business">Business</option>
                  <option value="personal">Personal</option>
                  <option value="education">Education</option>
                  <option value="software">Software Development</option>
                </select>
              </div>

              <div className="TemplateBuilderField">
                <label className="TemplateBuilderLabel">Audience</label>
                <input
                  type="text"
                  className="TemplateBuilderInput"
                  placeholder="e.g., Students, Teams, Individuals"
                  value={formData.audience}
                  onChange={(e) => handleChange('audience', e.target.value)}
                />
              </div>
            </div>

            <div className="TemplateBuilderField">
              <label className="TemplateBuilderLabel">Summary *</label>
              <textarea
                className="TemplateBuilderTextarea"
                rows={3}
                placeholder="Brief description of what this template includes..."
                value={formData.summary}
                onChange={(e) => handleChange('summary', e.target.value)}
              />
            </div>

            <div className="TemplateBuilderField">
              <label className="TemplateBuilderLabel">Tags</label>
              <div className="TemplateBuilderTagInput">
                <input
                  type="text"
                  className="TemplateBuilderInput"
                  placeholder="Add a tag..."
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddTag();
                    }
                  }}
                />
                <button
                  className="TemplateBuilderBtn TemplateBuilderBtnSecondary"
                  onClick={handleAddTag}
                >
                  Add Tag
                </button>
              </div>
              {formData.tags.length > 0 && (
                <div className="TemplateBuilderTags">
                  {formData.tags.map((tag, idx) => (
                    <span key={idx} className="TemplateBuilderTag">
                      {tag}
                      <button
                        className="TemplateBuilderTagRemove"
                        onClick={() => handleRemoveTag(tag)}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </section>

          {/* Pricing Section */}
          <section className="TemplateBuilderSection">
            <h3 className="TemplateBuilderSectionTitle">Pricing</h3>

            <div className="TemplateBuilderFieldGroup">
              <div className="TemplateBuilderField">
                <label className="TemplateBuilderLabel">Pricing Tier *</label>
                <select
                  className="TemplateBuilderSelect"
                  value={formData.pricing_tier}
                  onChange={(e) => {
                    handleChange('pricing_tier', e.target.value);
                    if (e.target.value === 'free') {
                      handleChange('price_gbp', '0.00');
                    }
                  }}
                >
                  <option value="free">Free</option>
                  <option value="premium">Premium</option>
                </select>
              </div>

              {formData.pricing_tier === 'premium' && (
                <div className="TemplateBuilderField">
                  <label className="TemplateBuilderLabel">Price (£ GBP) *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    className="TemplateBuilderInput"
                    placeholder="0.00"
                    value={formData.price_gbp}
                    onChange={(e) => handleChange('price_gbp', e.target.value)}
                  />
                </div>
              )}
            </div>
          </section>

          {/* Template Type & Content Section */}
          <section className="TemplateBuilderSection">
            <h3 className="TemplateBuilderSectionTitle">Template Configuration</h3>

            <div className="TemplateBuilderField">
              <label className="TemplateBuilderLabel">Template Type *</label>
              <select
                className="TemplateBuilderSelect"
                value={
                  formData.fields?.interactive && formData.fields?.visual
                    ? 'interactive_visual'
                    : formData.fields?.interactive
                    ? 'interactive_json'
                    : 'static'
                }
                onChange={(e) => {
                  if (e.target.value === 'interactive_visual') {
                    // Preserve existing data if switching from JSON to Visual
                    handleChange('fields', {
                      ...formData.fields,
                      interactive: true,
                      visual: true
                    });
                  } else if (e.target.value === 'interactive_json') {
                    // Preserve existing data, just remove visual flag
                    const newFields = { ...formData.fields, interactive: true };
                    delete newFields.visual;
                    handleChange('fields', newFields);
                  } else {
                    handleChange('fields', null);
                  }
                }}
              >
                <option value="static">Static (HTML only)</option>
                <option value="interactive_json">Interactive (JSON editor)</option>
                <option value="interactive_visual">Interactive (Visual builder)</option>
              </select>
              <p className="TemplateBuilderHint">
                Static templates use HTML content. Interactive templates prompt users for inputs.
                Use visual builder for drag-and-drop interface.
              </p>
            </div>

            {formData.fields?.interactive && formData.fields?.visual ? (
              <div className="TemplateBuilderField">
                <WYSIWYGTemplateBuilder
                  initialData={formData.fields}
                  onSave={(templateConfig) => {
                    handleChange('fields', { ...templateConfig, visual: true });
                  }}
                />
              </div>
            ) : formData.fields?.interactive ? (
              <div className="TemplateBuilderField">
                <label className="TemplateBuilderLabel">
                  Interactive Template JSON
                  <span className="TemplateBuilderLabelHint">
                    (Define variables and tasks with placeholders)
                  </span>
                </label>
                <textarea
                  className="TemplateBuilderTextarea TemplateBuilderCodeEditor"
                  rows={20}
                  placeholder={JSON.stringify({
                    variables: [
                      {
                        id: 'project_name',
                        type: 'text',
                        label: 'Project Name',
                        required: true,
                      },
                      {
                        id: 'deadline',
                        type: 'date',
                        label: 'Project Deadline',
                      },
                    ],
                    tasks: [
                      {
                        title: '{{project_name}} - Kickoff',
                        description: 'Start the project',
                        status: 'todo',
                      },
                      {
                        title: 'Final review',
                        description: 'Due: {{deadline}}',
                        status: 'todo',
                      },
                    ],
                  }, null, 2)}
                  value={typeof formData.fields === 'object' ? JSON.stringify(formData.fields, null, 2) : ''}
                  onChange={(e) => {
                    try {
                      const parsed = JSON.parse(e.target.value);
                      handleChange('fields', parsed);
                    } catch {
                      // Invalid JSON, update raw value anyway
                      handleChange('fields', e.target.value);
                    }
                  }}
                  spellCheck={false}
                />
              </div>
            ) : (
              <div className="TemplateBuilderField">
                <label className="TemplateBuilderLabel">
                  HTML Content
                  <span className="TemplateBuilderLabelHint">
                    (Use HTML for task descriptions, checklists, etc.)
                  </span>
                </label>
                <textarea
                  className="TemplateBuilderTextarea TemplateBuilderCodeEditor"
                  rows={12}
                  placeholder="<h3>Template Tasks</h3>&#10;<ul>&#10;  <li>Task 1</li>&#10;  <li>Task 2</li>&#10;</ul>"
                  value={formData.html}
                  onChange={(e) => handleChange('html', e.target.value)}
                  spellCheck={false}
                />
              </div>
            )}
          </section>

          {/* Publishing Options Section */}
          <section className="TemplateBuilderSection">
            <h3 className="TemplateBuilderSectionTitle">Publishing Options</h3>

            <div className="TemplateBuilderCheckboxGroup">
              <label className="TemplateBuilderCheckbox">
                <input
                  type="checkbox"
                  checked={formData.is_published}
                  onChange={(e) => handleChange('is_published', e.target.checked)}
                />
                <span>Publish to marketplace</span>
              </label>

              <label className="TemplateBuilderCheckbox">
                <input
                  type="checkbox"
                  checked={formData.featured}
                  onChange={(e) => handleChange('featured', e.target.checked)}
                />
                <span>Feature this template</span>
              </label>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
